import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ORGANIGRAM_TAG_COLOURS, ORGANIGRAM_UNTYPED_TAG } from 'Components/people/positionTypes';

import Alert from 'CommonComponents/Alert';
import IconButton from 'CommonComponents/IconButton';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import { ORGANIGRAM_DRAG_MIME, serverMessage } from 'Scripts/template';

import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import GripIcon from 'Assets/icons/grip.svg';
import SearchIcon from 'Assets/icons/search.svg';

/**
 * The positions in scope that are not linked into the reporting hierarchy at
 * either end, docked beside the org chart and dragged onto it to give one a
 * supervisor.
 *
 * The whole list arrives at once and is filtered in the browser. Real scopes
 * reach a bit over a hundred rows, which is small enough that a search box
 * beats pagination outright: the previous "+" button loaded five at a time and
 * gave no way at all to find a named role.
 *
 * Rows are HTML5 drag sources carrying only the position id under a private
 * MIME type — the canvas checks for that type on dragover to decide whether it
 * is looking at one of its own drags, and reads the id on drop.
 *
 * Rows are also drop TARGETS for each other. Dropping row A onto row B makes B
 * A's supervisor, and both then leave the panel as a two-node chart. That is
 * the only way to start a chart from a scope where everything is unassigned:
 * there is no node on the canvas to drop onto, and clearing an already-null
 * supervisor by dropping on blank canvas would change nothing.
 *
 * The panel as a whole is the drop target for the opposite direction — a card
 * dragged off the chart, which clears its supervisor and returns it to this
 * list. That drag is the chart library's own mouse drag rather than an HTML5
 * one and has already ended by the time the cursor arrives here, so the panel
 * reads a plain mouseup against the id the chart hands it, not drag events.
 *
 * The panel is absolutely positioned by its parent so that its height is the
 * canvas's, which is why the list below scrolls rather than growing: sized by
 * its own content it would stretch the page to a hundred rows tall and take the
 * chart with it.
 *
 * Props:
 *   fetchUrl      — endpoint returning { data: [{ id, name, type, tags }] }
 *   refreshToken  — changing this refetches, keeping the scroll position
 *   pendingId     — the row currently being assigned, greyed out until it lands
 *   collapsed     — owned by the parent, whose layout depends on the width
 *   onToggle      — (nextCollapsed) => void
 *   onAssign      — (positionId, supervisorId) => Promise, rejecting on refusal
 *   onCountChange — (count) => void, so the empty canvas can say whether the
 *                   chart is empty because everything is sitting in here
 *   incomingFromChartId — the card currently dragged off the chart, or null
 *   onUnassign    — (positionId) => Promise, rejecting on refusal
 */
export default function UnassignedPositionsPanel({
    fetchUrl,
    refreshToken = 0,
    pendingId = null,
    collapsed = false,
    onToggle,
    onCountChange,
    onAssign,
    incomingFromChartId = null,
    onUnassign,
}) {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    /** The row being dragged, so it is never offered as its own drop target. */
    const [draggingId, setDraggingId] = useState(null);
    const [dropTargetId, setDropTargetId] = useState(null);
    const [assignError, setAssignError] = useState(null);
    /** Whether the card dragged off the chart is currently over the panel. */
    const [chartCardOver, setChartCardOver] = useState(false);
    /**
     * Positions in the scope, not rows in this list — the two differ, and the
     * difference is the whole reason an empty panel needs explaining. Null when
     * the response did not carry one.
     */
    const [scopePositionCount, setScopePositionCount] = useState(null);

    const listRef = useRef(null);
    /**
     * Where the reader had scrolled to before the last refetch. A refresh can be
     * provoked by somebody else's assignment, and a list that jumps back to the
     * top when it happens loses the reader's place in it.
     */
    const scrollTopRef = useRef(0);
    /** First paint of a given scope shows a spinner; later refreshes do not blank the list. */
    const loadedOnceRef = useRef(false);

    const load = useCallback(() => {
        if (!fetchUrl) {
            setItems([]);
            setLoading(false);

            return;
        }

        scrollTopRef.current = listRef.current?.scrollTop ?? 0;
        setFailed(false);

        axios
            .get(fetchUrl)
            .then(({ data }) => {
                setItems(Array.isArray(data?.data) ? data.data : []);
                setScopePositionCount(typeof data?.meta?.scope_position_count === 'number' ? data.meta.scope_position_count : null);
            })
            .catch(() => {
                setFailed(true);
            })
            .finally(() => {
                loadedOnceRef.current = true;
                setLoading(false);
            });
    }, [fetchUrl]);

    // A new scope is a different list, so it starts at the top and shows a spinner.
    useEffect(() => {
        scrollTopRef.current = 0;
        loadedOnceRef.current = false;
        setSearch('');
        setLoading(true);
        load();
    }, [fetchUrl]);

    useEffect(() => {
        if (refreshToken > 0) {
            load();
        }
    }, [refreshToken]);

    useLayoutEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = scrollTopRef.current;
        }
    }, [items]);

    useEffect(() => {
        onCountChange?.(items.length);
    }, [items]);

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();

        return term === ''
            ? items
            : items.filter((item) =>
                  String(item.name ?? '')
                      .toLowerCase()
                      .includes(term),
              );
    }, [items, search]);

    /**
     * Why the panel is empty, which is not always "everything is placed".
     *
     * A scope of one has nothing for its position to report to, so the rule
     * leaves that position on the chart and this list empty — telling the
     * reader it has a reporting line would be flatly wrong. An absent count
     * reads as the ordinary case, so a response without one degrades to the
     * long-standing wording rather than to a confident wrong reason.
     */
    const emptyListMessage = () => {
        if (scopePositionCount === 0) {
            return $t('There are no positions here yet.');
        }

        if (scopePositionCount === 1) {
            return $t('Only one position here yet. Add another to start a reporting line.');
        }

        return $t('Every position here has a reporting line.');
    };

    const handleDragStart = (event, item) => {
        event.dataTransfer.setData(ORGANIGRAM_DRAG_MIME, String(item.id));
        event.dataTransfer.effectAllowed = 'move';
        setDraggingId(String(item.id));
    };

    const carriesPosition = (event) => Array.from(event.dataTransfer?.types ?? []).includes(ORGANIGRAM_DRAG_MIME);

    const handleRowDragOver = (event, item) => {
        // The id is only readable on drop, so the source row is identified from
        // state rather than from the payload.
        if (!carriesPosition(event) || draggingId === null || draggingId === String(item.id)) {
            return;
        }

        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropTargetId(String(item.id));
    };

    const handleRowDrop = (event, item) => {
        if (!carriesPosition(event)) {
            return;
        }

        event.preventDefault();

        const draggedId = event.dataTransfer.getData(ORGANIGRAM_DRAG_MIME);

        setDropTargetId(null);
        setDraggingId(null);

        if (!draggedId || draggedId === String(item.id)) {
            return;
        }

        setAssignError(null);

        Promise.resolve(onAssign?.(Number(draggedId), Number(item.id))).catch((error) => {
            setAssignError(serverMessage(error, $t('That position could not be assigned. Please try again.')));
        });
    };

    const acceptsChartCard = incomingFromChartId !== null && incomingFromChartId !== undefined;

    // The chart drops the arming as soon as the button is released anywhere, so
    // a highlight left over from a drag that ended elsewhere has to go with it.
    useEffect(() => {
        if (!acceptsChartCard) {
            setChartCardOver(false);
        }
    }, [acceptsChartCard]);

    /**
     * Release over the panel: clear the card's supervisor and bring it back to
     * this list. The chart's drag has already ended, so there is no drag
     * payload to read — the id comes from the chart, which is still holding it.
     */
    const chartCardDropProps = acceptsChartCard
        ? {
              onMouseEnter: () => setChartCardOver(true),
              onMouseLeave: () => setChartCardOver(false),
              onMouseUp: () => {
                  setChartCardOver(false);
                  setAssignError(null);

                  Promise.resolve(onUnassign?.(incomingFromChartId)).catch((error) => {
                      setAssignError(serverMessage(error, $t('That position could not be unassigned. Please try again.')));
                  });
              },
          }
        : {};

    /** Drawn over the panel while a chart card is held above it. */
    const chartCardHint = chartCardOver && (
        <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border-2 tw-border-dashed tw-border-blue tw-bg-blue tw-bg-opacity-10 tw-px-3 tw-text-center">
            <span className="tw-rounded tw-bg-white tw-px-2 tw-py-1 tw-text-2xs tw-font-semibold tw-text-blue tw-shadow-primary">
                {$t('Drop here to unassign')}
            </span>
        </div>
    );

    if (collapsed) {
        return (
            <div
                {...chartCardDropProps}
                className="tw-absolute tw-inset-y-0 tw-end-0 tw-flex tw-w-10 tw-flex-col tw-items-center tw-gap-y-2 tw-rounded-lg tw-border tw-border-solid tw-border-charcoal-25 tw-bg-white tw-py-3"
            >
                {chartCardHint}
                <IconButton ghost onClick={() => onToggle(false)} title={$t('Show unassigned positions')} aria-label={$t('Show unassigned positions')}>
                    <ChevronLeftIcon className="tw-w-3.5 tw-h-3.5" />
                </IconButton>
                <span className="tw-rounded-full tw-bg-charcoal tw-px-1.5 tw-py-0.5 tw-text-2xs tw-font-semibold tw-text-white">{items.length}</span>
            </div>
        );
    }

    return (
        <div
            {...chartCardDropProps}
            className="tw-absolute tw-inset-y-0 tw-end-0 tw-flex tw-w-60 tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-charcoal-25 tw-bg-white"
        >
            {chartCardHint}
            <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-x-2 tw-bg-charcoal tw-px-3 tw-py-2.5 tw-text-white">
                <span className="tw-text-0.5sm tw-font-semibold">{$t('Unassigned positions ({{count}})', { count: items.length })}</span>
                <button
                    type="button"
                    onClick={() => onToggle(true)}
                    title={$t('Collapse')}
                    aria-label={$t('Collapse')}
                    className="tw-flex tw-shrink-0 tw-items-center tw-gap-x-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-2xs tw-text-charcoal-25 hover:tw-text-white focus:tw-outline-none"
                >
                    {$t('Collapse')}
                    <ChevronRightIcon className="tw-w-2.5 tw-h-2.5" />
                </button>
            </div>

            <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-x-2 tw-m-3 tw-rounded-md tw-border tw-border-solid tw-border-charcoal-25 tw-px-2.5 tw-py-1.5">
                <SearchIcon className="tw-w-2.5 tw-h-2.5 tw-shrink-0 tw-text-charcoal-50" />
                <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={$t('Search positions')}
                    aria-label={$t('Search positions')}
                    className="tw-w-full tw-min-w-0 tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-text-charcoal placeholder:tw-text-charcoal-50 focus:tw-ring-0"
                />
            </div>

            {/*
             * Shown once the chart has been started, and only then: before that
             * the canvas carries the banner telling you to make the first
             * reporting line, and two sets of instructions at once help nobody.
             * A scope holding more positions than this list is the same thing as
             * "something is already on the chart".
             */}
            {items.length > 0 && typeof scopePositionCount === 'number' && scopePositionCount > items.length && (
                <p className="tw-mb-0 tw-shrink-0 tw-px-3 tw-pb-2 tw-text-2xs tw-text-charcoal-75">
                    {$t('Drag a position onto a card on the chart to give it a reporting line.')}
                </p>
            )}

            {assignError && (
                <div className="tw-shrink-0 tw-px-3 tw-pb-2">
                    <Alert type="error">
                        <span className="tw-flex tw-flex-col tw-items-start tw-gap-y-1">
                            <span>{assignError}</span>
                            <button
                                type="button"
                                onClick={() => setAssignError(null)}
                                className="tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-underline focus:tw-outline-none"
                            >
                                {$t('Dismiss')}
                            </button>
                        </span>
                    </Alert>
                </div>
            )}

            <div ref={listRef} className="tw-flex tw-flex-col tw-gap-y-2 tw-overflow-y-auto tw-px-3 tw-pb-3">
                {loading && !loadedOnceRef.current ? (
                    <div className="tw-py-6">
                        <LoadingSpinner />
                    </div>
                ) : failed ? (
                    <Alert type="error">
                        <span className="tw-flex tw-flex-col tw-items-start tw-gap-y-1">
                            <span>{$t('The unassigned positions could not be loaded.')}</span>
                            <button
                                type="button"
                                onClick={load}
                                className="tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-underline focus:tw-outline-none"
                            >
                                {$t('Try again')}
                            </button>
                        </span>
                    </Alert>
                ) : visible.length === 0 ? (
                    <p className="tw-mb-0 tw-py-6 tw-text-center tw-text-xs tw-text-charcoal-75">
                        {items.length === 0 ? emptyListMessage() : $t('No positions match your search.')}
                    </p>
                ) : (
                    visible.map((item) => {
                        const pending = String(item.id) === String(pendingId);
                        const dragging = String(item.id) === String(draggingId);
                        const isDropTarget = String(item.id) === String(dropTargetId);
                        const tag = (Array.isArray(item.tags) ? item.tags : []).find((candidate) =>
                            Object.prototype.hasOwnProperty.call(ORGANIGRAM_TAG_COLOURS, candidate),
                        );

                        return (
                            <div
                                key={item.id}
                                draggable={!pending}
                                onDragStart={(event) => handleDragStart(event, item)}
                                onDragEnd={() => {
                                    setDraggingId(null);
                                    setDropTargetId(null);
                                }}
                                onDragOver={(event) => handleRowDragOver(event, item)}
                                onDragLeave={() => setDropTargetId((current) => (current === String(item.id) ? null : current))}
                                onDrop={(event) => handleRowDrop(event, item)}
                                aria-disabled={pending}
                                className={`tw-relative tw-flex tw-flex-col tw-gap-y-0.5 tw-rounded-md tw-border tw-border-solid tw-bg-white tw-px-2.5 tw-py-2 ${
                                    isDropTarget ? 'tw-border-dashed tw-border-blue' : 'tw-border-grey'
                                } ${pending ? 'tw-cursor-progress tw-opacity-50' : 'tw-cursor-grab'} ${dragging ? 'tw-opacity-50' : ''}`}
                                style={{
                                    borderInlineStartWidth: 4,
                                    borderInlineStartColor: ORGANIGRAM_TAG_COLOURS[tag ?? ORGANIGRAM_UNTYPED_TAG],
                                }}
                            >
                                <span className="tw-flex tw-items-center tw-gap-x-2">
                                    <GripIcon className="tw-w-3 tw-h-3 tw-shrink-0 tw-text-charcoal-25" />
                                    <span className="tw-min-w-0 tw-text-xs tw-font-semibold tw-text-charcoal">{item.name}</span>
                                </span>
                                {/*
                                 * Drawn over the row, never inside its flow. In flow this hint is
                                 * an extra line, so the row grows the moment it becomes the target
                                 * and every row below shifts down — which moves the row out from
                                 * under the cursor, drops the hint, springs the list back, and
                                 * starts over. The list shakes and the drop is near impossible to
                                 * land. `pointer-events-none` is load-bearing too: an overlay that
                                 * takes pointer events swallows the dragover it is reacting to.
                                 */}
                                {isDropTarget && (
                                    <span className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-bg-white tw-bg-opacity-90 tw-px-2 tw-text-center tw-text-2xs tw-font-semibold tw-text-blue">
                                        {$t('Drop here to report to this position')}
                                    </span>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
